import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { firstValueFrom, Subscription } from 'rxjs';
import { ProjectVersion } from 'src/app/constants/project-version-constans';
import { FormatDetailModel } from 'src/app/models/formats/format-detail.model';
import { FormatModel } from 'src/app/models/formats/format.model';
import { QuotationPrintModel } from 'src/app/models/print/preliminaries/quotation-print.model';
import { FormatDetailService } from 'src/app/services/formats/format-detail.service';
import { PrintPageSizeService } from 'src/app/services/global-services/print-page-size.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { EnterpriseService } from 'src/app/services/management/enterprise.service';
import { QuotationDetailService } from 'src/app/services/preliminaries/quotation-detail.service';

@Component({
  selector: 'app-print-quotation',
  templateUrl: './print-quotation.component.html',
  styleUrls: ['./print-quotation.component.scss'],
  standalone: false
})

export class PrintQuotationComponent implements OnInit {
  @Input() companyId: number | null = 0;
  @Input() editFormat: boolean = false;
  @Input() exportFormat: boolean = false;
  @Input() focusText: boolean = false;
  @Input() formatDetail: FormatDetailModel = new FormatDetailModel();
  @Input() formatModel: FormatModel = new FormatModel();
  @Input() quotationPrintContract: QuotationPrintModel = new QuotationPrintModel();
  @Input() reloadPrint: boolean = false;
  @Output() emitFormatDetail = new EventEmitter<FormatDetailModel>();
  @Output() finishLoadingPrint = new EventEmitter<boolean>();

  bottomPreview: number = 0;
  columns: { field: string; header: string; }[] = [];
  idMap: { [key: string]: { id: string; type: string; showTotal: boolean; printFormat?: string | null; } } = {
    'codeCostCenter': { id: 'centro-costos', type: 'text', showTotal: false, printFormat: null },
    'solutionCode': { id: 'codigo-solucion', type: 'text', showTotal: false, printFormat: null },
    'solutionName': { id: 'nombre-solucion', type: 'text', showTotal: false, printFormat: null },
    'rentalUnit': { id: 'unidad-medida', type: 'text', showTotal: false, printFormat: null },
    'unitValue': { id: 'valor-unitario', type: 'decimal', showTotal: false, printFormat: null },
    'quantity': { id: 'cantidad', type: 'decimal', showTotal: false, printFormat: null },
    'subtotalValue': { id: 'subtotal', type: 'decimal', showTotal: false, printFormat: null },
    'days': { id: 'dias', type: 'decimal', showTotal: false, printFormat: null },
    'totalValue': { id: 'valor-total', type: 'decimal', showTotal: false, printFormat: null },
    'ivaValue': { id: 'valor-iva', type: 'decimal', showTotal: false, printFormat: null },
    'totalWithIva': { id: 'valor-total-iva', type: 'decimal', showTotal: false, printFormat: null }
  };
  columnsBreakdown: { field: string; header: string; }[] = [];
  idMapBreakdown: { [key: string]: { id: string; type: string; showTotal: boolean; printFormat?: string | null; } } = {
    'articleCode': { id: 'articulo', type: 'text', showTotal: false, printFormat: null },
    'articleName': { id: 'articulo-nombre', type: 'text', showTotal: false, printFormat: null },
    'clientValue': { id: 'valor-unitario-despiece', type: 'decimal', showTotal: false, printFormat: null },
    'quantity': { id: 'cantidad-despiece', type: 'decimal', showTotal: false, printFormat: null },
    'subtotalBreakdown': { id: 'valor-subtotal', type: 'decimal', showTotal: false, printFormat: null },
    'weight': { id: 'peso-unitario', type: 'decimal', showTotal: false, printFormat: null },
    'replacementValue': { id: 'valor-unitario-reposicion', type: 'decimal', showTotal: false, printFormat: null },
  };
  dateNow: Date = new Date();
  element: any;
  elementStyle: any;
  elementsBody: FormatDetailModel[] = [];
  elementsBodyBreakdown: FormatDetailModel[] = [];
  elementsLabel: FormatDetailModel[] = [];
  id = '';
  isShift = false;
  leftPreview: number = 0;
  listFormatDetailUpdates: FormatDetailModel[] = [];
  listObservers: Array<Subscription> = new Array<Subscription>();
  logoEnterprise: SafeUrl = "";
  mouseDown: boolean = false;
  newFormatDetail: FormatDetailModel = new FormatDetailModel();
  rightPreview: number = 0;
  shiftX: number = 0;
  shiftY: number = 0;
  speed = 2;
  titleForm: string = '';
  topPreview: number = 0;
  userAuthenticated: string = '';
  version: string = ProjectVersion.projectVersion;

  private keydownListener!: (e: KeyboardEvent) => void;
  private mouseupListener!: () => void;
  private mousemoveListener!: (e: MouseEvent) => void;
  private keyupListener!: (e: KeyboardEvent) => void;

  constructor(
    private enterpriseService: EnterpriseService,
    private formatDetailService: FormatDetailService,
    private quotationDetailService: QuotationDetailService,
    private sanitizer: DomSanitizer,
    private userAuthenticatedService: UserAuthenticatedService,
    private printPageSizeService: PrintPageSizeService
  ) { }

  ngOnInit() {
    this.getUserAutheticated()
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const exportFormat = changes["exportFormat"];
    const formatDetail = changes["formatDetail"];
    const formatModel = changes["formatModel"];
    const quotationPrintContract = changes["quotationPrintContract"];

    if (quotationPrintContract && !quotationPrintContract.firstChange) {
      await this.configureFormatByDocument();
    }

    if (exportFormat && !exportFormat.firstChange) { this.updateRangeFormatDetail(); }
    if (formatModel && formatModel.firstChange) { this.formatModel = formatModel.currentValue; }
    if (formatDetail && !formatDetail.firstChange) {
      this.changeFormatDetail();
    }
  }

  ngOnDestroy() {
    this.listObservers.forEach(sub => sub.unsubscribe());
    this.printPageSizeService.removePageSizeStyle();

    // Remover event listeners globales
    if (this.keydownListener) document.removeEventListener('keydown', this.keydownListener);
    if (this.mouseupListener) document.removeEventListener('mouseup', this.mouseupListener, true);
    if (this.mousemoveListener) document.removeEventListener('mousemove', this.mousemoveListener, true);
    if (this.keyupListener) document.removeEventListener('keyup', this.keyupListener);
  }

  ngAfterViewInit() {
    if (this.editFormat) {
      this.keydownListener = (e: KeyboardEvent) => {
        const target = e.target as HTMLElement;
        if (
          target &&
          (target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.tagName === 'SELECT' ||
            target.isContentEditable)
        ) {
          return;
        }
        // 2. Si no hay ningún elemento seleccionado para mover, no bloquear
        if (!this.element) {
          return;
        }

        // Deshabilita desplazamiento del scroll con la tecla espacio y flechas
        if (["Space", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].indexOf(e.code) > -1) {
          this.blockScrollArrows(e);
        }

        if (e.which == 16) this.isShift = true;
        //Mover element con flechas
        if (!this.isShift) {
          switch (e.key) {
            case 'ArrowLeft': { this.moveLeft(); break; }
            case 'ArrowRight': { this.moveRight(); break; }
            case 'ArrowUp': { this.moveUp(); break; }
            case 'ArrowDown': { this.moveDown(); break; }
          }
        }

        //Modificar element tamaño de objeto
        if (this.isShift) {
          switch (e.which) {
            case 37: { this.decreaseWidth(); break; }
            case 38: { this.decreaseHeight(); break; }
            case 39: { this.increaseWidth(); break; }
            case 40: { this.increaseHeight(); break; }
          }
        }
      };

      this.mouseupListener = () => {
        this.onMouseUp();
      };

      this.mousemoveListener = (e: MouseEvent) => {
        this.onMouseMove(e);
      };

      this.keyupListener = (e: KeyboardEvent) => {
        if (e.which == 16) this.isShift = false;
      };

      // Registrar los listeners en el document
      document.addEventListener('keydown', this.keydownListener);
      document.addEventListener('mouseup', this.mouseupListener, true);
      document.addEventListener('mousemove', this.mousemoveListener, true);
      document.addEventListener('keyup', this.keyupListener);
    }
  }

  private adjustElementSize(property: 'height' | 'width', delta: number) {
    if (this.focusText) return;
    if (this.id.includes('observacion-cotizacion-suc') && this.id.includes('condiciones-comerciales') && property == 'height') return;

    const currentValue = this.elementStyle.getPropertyValue(property);
    const numericValue = parseFloat(currentValue.replace('px', ''));

    const newValue = numericValue + delta;
    this.element.style[property] = `${newValue}px`;

    this.onClick();
  }

  private async applyColumnStyles(quotation: QuotationPrintModel) {

    // Encabezado de tabla
    for (const column of this.columns.map(x => x.field)) {
      const element = this.elementsBody.find(x => x.description === column);
      if (!element) continue;

      for (const detail of quotation.quotationDetails) {
        this.element = document.getElementById(`${element.name}-${detail.quotationDetailId}`);
        if (this.element) {
          this.element.style.fontSize = `${element.fontSize}px`;
          this.element.style.width = `${element.width}%`;
          await this.changeAlignment(element.alignment);
        }
      }
    }

    // Despiece (encabezados)
    for (const column of this.columnsBreakdown.map(x => x.field)) {
      for (const element of this.formatModel.formatDetails) {
        if (column !== element.description) continue;

        await this.groupingClasses(element);
      }
    }
  }

  private applyElementStyles(element: FormatDetailModel) {
    if (!element.visible) {
      this.element!.setAttribute('hidden', '');
      return;
    }

    const style = this.element!.style;
    style.fontSize = `${element.fontSize}px`;
    this.changeAlignment(element.alignment);
    this.changeBold(element.bold);
    this.changeBorder(element.border);

    if (element.name.includes('foot-')) {
      style.height = `${element.height}px`;
      style.width = `${element.width}px`;
    } else {
      style.backgroundColor = 'transparent';
      style.width = `${element.width}px`;
      if (((!element.name.includes('observacion-cotizacion-suc') && !element.name.includes('condiciones-comerciales')))) {
        style.position = 'absolute';
        style.left = `${element.left}%`;
        style.top = `${element.top}%`;
        style.height = `${element.height}px`;
      }
    }
  }


  private async applyFormatDetails(elements: FormatDetailModel[], quotation: QuotationPrintModel) {
    for (const element of elements) {
      if (element.band === 'body' || element.band === 'bodyBd') continue;
      setTimeout(async () => {
        this.element = document.getElementById(`${element.name}-${quotation.quotationId}`);
        if (this.element) {
          this.applyElementStyles(element);
        }
      }, 200);
    }
  }

  private applyFormatStyles(element: HTMLElement) {
    element.style.fontSize = `${this.formatDetail.fontSize}px`;
    this.changeAlignment(this.formatDetail.alignment);
    this.changeBold(this.formatDetail.bold);
    this.changeBorder(this.formatDetail.border);

    //No permito mover elementos totales y de la observacion de sucursal
    if (!this.formatDetail.name.includes('foot-') && !this.formatDetail.name.includes('observacion-cotizacion-suc')
      && !this.formatDetail.name.includes('condiciones-comerciales')) {
      this.element.style.position = 'absolute';
      this.element.style.left = `${this.formatDetail.left}%`;
      this.element.style.top = `${this.formatDetail.top}%`;
    }

    //No permito cambiar altura de elementos de la observacion de sucursal
    if (!this.formatDetail.name.includes('observacion-cotizacion-suc') && !this.formatDetail.name.includes('condiciones-comerciales')) {
      element.style.height = `${this.formatDetail.height}px`;
    }
    element.style.width = `${this.formatDetail.width}px`;
  }

  private applyHeaderFooterHeights(quotationId: number, formatModel: FormatModel) {
    setTimeout(async () => {
      const head = document.getElementById(`head-${quotationId}`);
      const foot = document.getElementById(`foot-${quotationId}`);
      head!.style.height = `${formatModel.head}px`;
      foot!.style.height = `${formatModel.foot}px`;
    }, 200);
  }

  async applyParagraphStyles(quotation: QuotationPrintModel) {

    this.elementsBody
      .filter(x => x.type === 'p')
      .forEach(element => {
        quotation.quotationDetails!.forEach(detail => {
          const id = element.name.includes("footer") ? detail.quotationId : detail.quotationDetailId;
          this.element = document.getElementById(`${element.name}-${id}`);
          if (this.element) {
            this.element.style.fontSize = `${element.fontSize}px`;
            this.changeAlignment(element.alignment);
            this.changeBold(element.bold);
            if (element.name.includes("td-nombre-solucion")) this.element.style.whiteSpace = `pre-line`;
          }
        });
      });

    // Despiece
    for (const element of this.elementsBodyBreakdown.filter(x => x.type === 'p')) {
      for (const detail of quotation.quotationDetails) {
        if (!detail.breakdownPrints) continue;

        for (const breakdown of detail.breakdownPrints) {
          this.element = document.getElementById(`${element.name}-${breakdown.breakdownId}`);
          if (this.element) {
            this.element.style.fontSize = `${element.fontSize}px`;
            await this.changeAlignment(element.alignment);
            await this.changeBold(element.bold);
          }
        }
      }
    }
  }

  blockScrollArrows(e: any) {
    if (!this.focusText) {
      e.preventDefault();
    }
  }

  private buildDisplayedColumns() {
    this.columns = [];
    this.elementsBody.filter(x => x.type === 'label').forEach(element => {
      this.columns.push({ field: element.description!, header: element.text! });;
      this.idMap[element.description!].printFormat = element.printFormat ?? null;
    });

    this.columnsBreakdown = [];
    this.elementsBodyBreakdown.filter(x => x.type === 'label').forEach(element => {
      this.columnsBreakdown.push({ field: element.description!, header: element.text! });
      this.idMapBreakdown[element.description!].printFormat = element.printFormat ?? null;
    });
  }

  private async buildImageUrl(quotationDetailId: number, imagePath: string): Promise<SafeUrl> {
    const blob = await firstValueFrom(this.quotationDetailService.getImageUrl(quotationDetailId, imagePath));
    const objectURL = URL.createObjectURL(blob);
    return this.sanitizer.bypassSecurityTrustUrl(objectURL);
  }

  changeAlignment(alignment: string) {
    this.element.style.textAlign = alignment;
  }

  changeBold(bold: string) {
    this.element.style.fontWeight = bold;
  }

  changeBorder(border: string) {
    switch (border) {
      case 'noborder': {
        this.element.style.border = '0px none rgb(0, 0, 0)';
        break;
      }
      case 'border': {
        this.element.style.border = '1px solid rgb(0, 0, 0)';
        break;
      }
      case 'underline': {
        this.element.style.border = '0px none rgb(0, 0, 0)';
        this.element.style.borderBottom = '1px solid rgb(0, 0, 0)';
        break;
      }
    }
  }

  changeFormatDetail() {
    const formatDetailId = this.formatDetail.formatDetailId;
    if (formatDetailId == 0) return;

    const isBody = this.formatDetail.band === 'body';
    const isBodyBd = this.formatDetail.band === 'bodyBd';
    const isLabel = this.formatDetail.type === 'label';

    if (isBody) {
      const element = this.elementsBody.find(x => x.formatDetailId === formatDetailId);
      if (!element) return;

      element.alignment = this.formatDetail.alignment;
      element.fontSize = this.formatDetail.fontSize;
      element.printFormat = this.formatDetail.printFormat;

      if (isLabel) {
        element.visible = this.formatDetail.visible;
        element.width = this.formatDetail.width;
        element.text = this.formatDetail.text;

        if (!this.formatDetail.visible) {
          this.columns = this.columns.filter(x => x.field !== element.description);
        } else {
          this.quotationPrintContract.quotationDetails.forEach(detail => {
            this.updateElementWidth(this.formatDetail.name, detail.quotationDetailId!);
            this.updateElementStyle(this.formatDetail.name, detail.quotationDetailId!);
            this.changeAlignment(this.formatDetail.alignment);
          });
        }
      } else {
        element.bold = this.formatDetail.bold;
        this.quotationPrintContract.quotationDetails!.forEach(detail => {
          if (detail.quotationDetailId) {
            const id = this.formatDetail.name.includes("footer") ? detail.quotationId : detail.quotationDetailId;
            this.updateElementStyle(this.formatDetail.name, id);
            this.changeAlignment(this.formatDetail.alignment);
            this.changeBold(this.formatDetail.bold);
          }
        });
      }

      this.elementsBody = this.elementsBody.filter(x => x.formatDetailId !== formatDetailId);
      this.elementsBody.push(element);
      const fieldName = element.description;
      if (fieldName && this.idMap[fieldName]) {
        this.idMap[fieldName].printFormat = element.printFormat;
      }
    } else if (isBodyBd) {
      //Despiece
      const element = this.elementsBodyBreakdown.find(x => x.formatDetailId === formatDetailId);
      if (!element) return;
      element.alignment = this.formatDetail.alignment;
      element.fontSize = this.formatDetail.fontSize;
      element.printFormat = this.formatDetail.printFormat;

      if (isLabel) {
        element.visible = this.formatDetail.visible;
        element.width = this.formatDetail.width;
        element.text = this.formatDetail.text;

        if (!this.formatDetail.visible) {
          this.columnsBreakdown = this.columnsBreakdown.filter(x => x.field !== element.description);
        } else {
          this.groupingClasses(this.formatDetail);
        }
      } else {
        element.bold = this.formatDetail.bold;
        this.quotationPrintContract.quotationDetails!.forEach(detail => {
          detail.breakdownPrints!.forEach(breakdown => {
            if (breakdown.breakdownId) {
              const id = breakdown.breakdownId;
              this.updateElementStyle(this.formatDetail.name, id);
              this.changeAlignment(this.formatDetail.alignment);
              this.changeBold(this.formatDetail.bold);
            }
          });
        });
      }
      this.elementsBody = this.elementsBody.filter(x => x.formatDetailId !== formatDetailId);
      this.elementsBody.push(element);
      const fieldName = element.description;
      if (fieldName && this.idMapBreakdown && this.idMapBreakdown[fieldName]) {
        this.idMapBreakdown[fieldName].printFormat = element.printFormat;
      }
    } else {
      this.updateElementStyle(this.formatDetail.name, this.quotationPrintContract.quotationId!);
      if (!this.formatDetail.visible) {
        this.element.setAttribute('hidden', '');
      } else {
        this.applyFormatStyles(this.element);
        this.updateLabelText(formatDetailId, this.formatDetail.text!);
        this.listFormatDetailUpdates = this.listFormatDetailUpdates.filter(x => x.formatDetailId !== formatDetailId);
        this.listFormatDetailUpdates.push(this.formatDetail);
      }
      const labelElement = this.elementsLabel.find(x => x.name === this.formatDetail.name);
      if (labelElement) {
        labelElement.printFormat = this.formatDetail.printFormat;
        this.elementsLabel = [...this.elementsLabel];
      }
    }
  }

  async chargeFormat(formatModel: FormatModel): Promise<boolean> {
    return new Promise((resolve) => {
      (async () => {
        this.formatModel = formatModel;
        this.printPageSizeService.setPageSizeStyle(formatModel.paperSizeId);

        this.applyHeaderFooterHeights(this.quotationPrintContract.quotationId!, formatModel);
        this.extractFormatDetails(formatModel);

        this.buildDisplayedColumns();
        await this.applyFormatDetails(formatModel.formatDetails, this.quotationPrintContract);

        setTimeout(() => {
          this.applyColumnStyles(this.quotationPrintContract);
          this.applyParagraphStyles(this.quotationPrintContract);
          resolve(true);
        }, 200);
      })();
    });
  }

  async chargeImages(quotationDetailId: number, imagePaths: any) {
    const previews = [];
    for (let i = 0; i < imagePaths.length; i++) {
      if (imagePaths[i]) {
        previews[i] = await this.buildImageUrl(quotationDetailId, imagePaths[i]!);
      }
    }
    return previews;
  }

  clickTable(event: any) {
    if (!this.editFormat) return;

    const element = document.getElementById(event.target.id);
    if (!element) return;

    const elementStyle = window.getComputedStyle(element);
    const name = event.target.id.substring(0, event.target.id.lastIndexOf('-'));
    const formatDetail = this.formatModel.formatDetails.find(x => x.name === name);
    const bodyElement = this.elementsBody.find(x => x.name === name);
    const bodyBreakdownElement = this.elementsBodyBreakdown.find(x => x.name === name);

    if (!formatDetail) return;
    this.newFormatDetail = new FormatDetailModel();
    this.newFormatDetail.name = name;
    this.newFormatDetail.formatDetailId = formatDetail.formatDetailId;
    this.newFormatDetail.formatId = this.formatModel.formatId;
    this.newFormatDetail.description = formatDetail.description;
    this.newFormatDetail.band = formatDetail.band;
    this.newFormatDetail.type = formatDetail.type;
    this.newFormatDetail.printFormat = formatDetail.printFormat;
    if (bodyElement) {
      this.newFormatDetail.visible = bodyElement.visible;
      this.newFormatDetail.text = bodyElement.text;
      this.newFormatDetail.orderTable = bodyElement.orderTable;
      this.newFormatDetail.printFormat = bodyElement.printFormat;
    }

    if (bodyBreakdownElement) {
      this.newFormatDetail.visible = bodyBreakdownElement.visible;
      this.newFormatDetail.text = bodyBreakdownElement.text;
      this.newFormatDetail.orderTable = bodyBreakdownElement.orderTable;
      this.newFormatDetail.printFormat = bodyBreakdownElement.printFormat;
    }

    // Ancho
    if (formatDetail.type === 'label') {
      const rawWidth = element.style.width;
      const width = parseFloat(rawWidth.replace('%', '')) || 0;
      this.newFormatDetail.width = parseFloat(width.toFixed(4));
    } else {
      this.newFormatDetail.width = 0;
    }

    // Tamaño de fuente
    const fontSize = parseInt(elementStyle.getPropertyValue('font-size').replace('px', ''));
    this.newFormatDetail.fontSize = isNaN(fontSize) ? 0 : fontSize;

    // Negrita
    const fontWeight = elementStyle.getPropertyValue('font-weight');
    this.newFormatDetail.bold = fontWeight === '400' ? 'normal' : 'bold';

    // Alineación
    this.newFormatDetail.alignment = elementStyle.getPropertyValue('text-align');
    this.emitFormatDetail.emit(this.newFormatDetail);
  }

  async configureFormatByDocument() {
    const enterpriseLogo = this.quotationPrintContract.enterpriseLogo;
    if (enterpriseLogo == null) {
      this.getLogoDefault();
    } else {
      await this.getLogo(enterpriseLogo);
    }

    const visibleImages = this.formatModel.formatDetails.find(x => x.name == 'content-images')?.visible ?? false;
    await Promise.all(
      this.quotationPrintContract.quotationDetails!.map(async (detail, index) => {
        index += 1;
        detail.index = index;
        if (visibleImages) {
          detail.previews = await this.chargeImages(detail.quotationDetailId, [detail.image1, detail.image2, detail.image3, detail.image4]);
        }
      })
    );

    this.chargeFormat(this.formatModel).then(() => {
      this.dateNow = new Date();
      this.finishLoadingPrint.emit(true);
    });
  }

  decreaseHeight() {
    this.adjustElementSize('height', -this.speed);
  }

  increaseHeight() {
    this.adjustElementSize('height', this.speed);
  }

  decreaseWidth() {
    this.adjustElementSize('width', -this.speed);
  }

  increaseWidth() {
    this.adjustElementSize('width', this.speed);
  }

  onColumnReorder(columns: { field: string; header: string; }[]) {
    columns.forEach((col, index) => {
      this.elementsBody.find(x => x.description == col.field)!.orderTable = index;
    });
  }

  onColumnReorderBreakdown(columns: { field: string; header: string; }[]) {
    columns.forEach((col, index) => {
      this.elementsBodyBreakdown.find(x => x.description == col.field)!.orderTable = index;
    });
  }

  private extractFormatDetails(formatModel: FormatModel) {
    this.elementsLabel = formatModel.formatDetails.filter(x => x.type === 'label');

    //Detalle
    this.elementsBody = formatModel.formatDetails
      .filter(x => x.band === 'body' && x.visible)
      .sort((a, b) => a.orderTable - b.orderTable);

    //Despiece
    this.elementsBodyBreakdown = formatModel.formatDetails
      .filter(x => x.band === 'bodyBd' && x.visible)
      .sort((a, b) => a.orderTable - b.orderTable);
  }

  getLabelPrintFormat(name: string): string | null {
    return this.elementsLabel.find(x => x.name === name)?.printFormat ?? null;
  }

  async getLogo(enterpriseLogo: string) {
    let blob: Blob | null = null;
    try {
      blob = await firstValueFrom(this.enterpriseService.getEnterpriseLogoAsync(enterpriseLogo));
      if (blob) {
        const objectURL = URL.createObjectURL(blob);
        this.logoEnterprise = this.sanitizer.bypassSecurityTrustUrl(objectURL);
      }
    } catch (error) {
      console.error('Error al obtener el logo de la empresa.', error);
    }
  }

  async getLogoDefault() {
    const logoPath = 'assets/images/icons/logo-empty.png';
    this.logoEnterprise = this.sanitizer.bypassSecurityTrustUrl(logoPath);
  }

  getUserAutheticated() {
    return new Promise((resolve) => {
      this.userAuthenticatedService.userObservable.subscribe(value => {
        this.userAuthenticated = value.userName;
      });
      resolve(true);
    });
  }

  groupingClasses(element: FormatDetailModel) {
    let classEdit: any
    switch (element.name) {
      case "th-articulo": {
        classEdit = document.querySelectorAll('.articulo');
        this.setGroupingStyles(element, classEdit);
        break;
      }
      case "th-articulo-nombre": {
        classEdit = document.querySelectorAll('.articulo-nombre');
        this.setGroupingStyles(element, classEdit);
        break;
      }
      case "th-valor-unitario-despiece": {
        classEdit = document.querySelectorAll('.valor-unitario-despiece');
        this.setGroupingStyles(element, classEdit);
        break;
      }
      case "th-cantidad-despiece": {
        classEdit = document.querySelectorAll('.cantidad-despiece');
        this.setGroupingStyles(element, classEdit);
        break;
      }
      case "th-valor-subtotal": {
        classEdit = document.querySelectorAll('.valor-subtotal');
        this.setGroupingStyles(element, classEdit);
        break;
      }
      case "th-peso-unitario": {
        classEdit = document.querySelectorAll('.peso-unitario');
        this.setGroupingStyles(element, classEdit);
        break;
      }
      case "th-valor-unitario-reposicion": {
        classEdit = document.querySelectorAll('.valor-unitario-reposicion');
        this.setGroupingStyles(element, classEdit);
        break;
      }
    }
  }

  isGroup(index: any, item: any): boolean {
    return item.level;
  }

  private updateElementPosition(leftPx: number, topPx: number) {
    const leftPercentage = ((leftPx / this.rightPreview) * 100).toFixed(4);
    const topPercentage = ((topPx / this.bottomPreview) * 100).toFixed(4);

    this.element.style.left = `${leftPercentage}%`;
    this.element.style.top = `${topPercentage}%`;
  }

  moveUp() {
    this.moveElement('top', -this.speed, this.bottomPreview, true);
  }

  moveDown() {
    this.moveElement('top', this.speed, this.bottomPreview, true);
  }

  moveLeft() {
    this.moveElement('left', -this.speed, this.rightPreview, false);
  }

  moveRight() {
    this.moveElement('left', this.speed, this.rightPreview, false);
  }

  private moveElement(direction: 'top' | 'left', delta: number, previewLimit: number, isVertical: boolean) {
    if (this.focusText || !this.elementStyle) return;

    const currentValue = this.elementStyle.getPropertyValue(direction);
    let position = parseFloat(currentValue.replace('px', ''));

    position += delta;

    const sizeProp = isVertical ? 'height' : 'width';
    const size = parseFloat(this.elementStyle.getPropertyValue(sizeProp).replace('px', ''));

    // Restringir dentro del contenedor
    if (position < 0) position = 0;
    if (position + size > previewLimit) position = previewLimit - size;

    const percentage = ((position / previewLimit) * 100).toFixed(4);
    this.element.style[direction] = `${percentage}%`;

    this.onClick();
  }

  onClick() {
    if (!this.editFormat) return;
    const name = this.id.substring(0, this.id.lastIndexOf('-'));
    const formatDetail = this.formatModel.formatDetails.find(x => x.name === name);
    if (!formatDetail) return;

    this.newFormatDetail = new FormatDetailModel();
    this.newFormatDetail.name = name;
    this.newFormatDetail.formatDetailId = formatDetail.formatDetailId;
    this.newFormatDetail.formatId = this.formatModel.formatId;
    this.newFormatDetail.band = formatDetail.band;
    this.newFormatDetail.description = formatDetail.description;
    this.newFormatDetail.text = formatDetail.text;
    this.newFormatDetail.visible = true;
    this.newFormatDetail.printFormat = formatDetail.printFormat;

    if (!this.id.includes('content-images')) {
      this.newFormatDetail.fontSize = this.getStylePxValue('font-size');
      this.newFormatDetail.bold = this.elementStyle.getPropertyValue('font-weight') === '400' ? 'normal' : 'bold';
      this.newFormatDetail.alignment = this.elementStyle.getPropertyValue('text-align');
      this.newFormatDetail.border = this.getBorderStyle();

      //No permito mover elementos totales y de la observacion de sucursal
      if (!name.includes('foot-') && !name.includes('observacion-cotizacion-suc') && !name.includes('condiciones-comerciales')) {
        this.newFormatDetail.left = parseFloat(((this.getStylePxValue('left') / this.rightPreview) * 100).toFixed(4));
        this.newFormatDetail.top = parseFloat(((this.getStylePxValue('top') / this.bottomPreview) * 100).toFixed(4));
        this.newFormatDetail.height = parseFloat(this.getStylePxValue('height').toFixed(4));
      }

      this.newFormatDetail.width = parseFloat(this.getStylePxValue('width').toFixed(4));
    }
    this.emitFormatDetail.emit(this.newFormatDetail);
    this.listFormatDetailUpdates = this.listFormatDetailUpdates.filter(x => x.formatDetailId !== this.newFormatDetail.formatDetailId);
    this.listFormatDetailUpdates.push(this.newFormatDetail);
  }

  private getStylePxValue(property: string): number {
    const value = this.elementStyle.getPropertyValue(property);
    return parseFloat(value.replace('px', '')) || 0;
  }

  private getBorderStyle(): string {
    //Borde
    if (this.elementStyle.getPropertyValue('border')) {
      if (this.elementStyle.getPropertyValue('border') === '0px none rgb(0, 0, 0)') {
        return 'noborder';
      } else {
        return 'border';
      }
    } else {
      return 'underline';
    }
  }

  onDragStart() {
    return false;
  }

  onMouseDown(event: any) {
    if (!this.editFormat) return;

    if (this.element) {
      this.element.style.backgroundColor = 'transparent';
    }

    this.mouseDown = true;
    this.id = event.target.id;
    this.element = document.getElementById(this.id);
    if (!this.element) return;

    this.elementStyle = window.getComputedStyle(this.element);
    this.element.style.backgroundColor = '#0d6efd6e';

    const name = this.id.substring(0, this.id.lastIndexOf('-'));
    const formatDetail = this.formatModel.formatDetails.find(x => x.name === name);

    if (!formatDetail) return;

    if (!this.id.includes('content-images')) {
      const relativeElement = document.getElementById(`${formatDetail.band}-${this.quotationPrintContract.quotationId}`);

      if (!relativeElement) return;

      const relativeStyle = window.getComputedStyle(relativeElement);

      this.leftPreview = Math.round(relativeElement.offsetLeft - relativeElement.scrollLeft);
      this.topPreview = Math.round(relativeElement.offsetTop - relativeElement.scrollTop);
      this.rightPreview = parseInt(relativeStyle.width.replace('px', ''));
      this.bottomPreview = parseInt(relativeStyle.height.replace('px', ''));

      //No permito mover elementos totales y  observacion de sucursal
      if (!this.id.includes('foot-') && !this.id.includes('observacion-cotizacion-suc') && !this.id.includes('condiciones-comerciales')) {
        this.element.style.cursor = 'move';

        const leftPX = event.pageX - this.leftPreview;
        const topPX = event.pageY - this.topPreview;

        this.shiftX = leftPX - this.getStylePx('left');
        this.shiftY = topPX - this.getStylePx('top');
      }
    }

    this.onClick();
  }

  onMouseMove(event: any) {
    if (this.mouseDown && !this.id.includes('foot-') && !this.id.includes('observacion-cotizacion-suc') &&
      !this.id.includes('condiciones-comerciales') && !this.id.includes('content-images')) {
      this.element.style.position = 'absolute';
      const leftPx = Math.max(0, event.pageX - this.leftPreview - this.shiftX);
      const widthPx = this.getStylePx('width');
      const finalLeft = Math.min(leftPx, this.rightPreview - widthPx);

      const topPx = Math.max(0, event.pageY - this.topPreview - this.shiftY);
      const heightPx = this.getStylePx('height');
      const finalTop = Math.min(topPx, this.bottomPreview - heightPx);

      this.updateElementPosition(finalLeft, finalTop);
      this.onClick();
    }

  }

  setGroupingStyles(element: FormatDetailModel, classEdit: any) {
    classEdit.forEach((el: HTMLElement) => {
      el.style.fontSize = `${element.fontSize}px`;
      el.style.width = `${element.width}%`;
      el.style.textAlign = element.alignment;
      el.style.fontWeight = element.bold;
    });
  }

  private getStylePx(property: string): number {
    return parseFloat(this.elementStyle.getPropertyValue(property).replace('px', '')) || 0;
  }

  private updateElementStyle(name: string, id: string | number) {
    this.element = document.getElementById(`${name}-${id}`);
    if (!this.element) return;
    this.element.style.fontSize = `${this.formatDetail.fontSize}px`;
  }

  private updateElementWidth(name: string, id: string | number) {
    this.element = document.getElementById(`${name}-${id}`);
    if (!this.element) return;
    this.element.style.width = `${this.formatDetail.width}%`;
  }

  private updateLabelText(formatId: number, text: string) {
    const labelElement = this.elementsLabel.find(x => x.formatDetailId === formatId);
    if (labelElement) {
      labelElement.text = text;
    }
  }

  async updateRangeFormatDetail() {
    this.elementsBody.forEach(element => {
      this.listFormatDetailUpdates.push(element);
    });

    this.elementsBodyBreakdown.forEach(element => {
      this.listFormatDetailUpdates.push(element);
    });

    const response = await firstValueFrom(this.formatDetailService.updateRangeFormatDetail(this.listFormatDetailUpdates));
    this.chargeFormat(response);
  }

  onMouseUp() {
    this.mouseDown = false;
  }

  visibleModal() {
    //Después de imprmir muesto modal de captura
    const backdrop = document.querySelectorAll(".modal-backdrop.fade");
    backdrop.forEach(element => {
      element.classList.add("show");
    });

    const block = document.querySelectorAll(".d-block.modal.fade");
    block.forEach(element => {
      element.classList.add("show");
    });
  }
}
